import React, { useEffect, useState, useCallback } from "react";
import { fetchCandidates, patchCandidate } from "../../../services/candidateService";
import { CandidateListItem } from "../../../types/fyo";
import { useAuth } from "../../../context/useAuth";
import PageHeader from "../../shared/PageHeader/PageHeader";
import Badge from "../../shared/Badge/Badge";
import { formatDateLong } from "../../../utils/formatters";
import "./CandidatesPage.css";

const LIMIT = 50;

interface Props {
  onSelect: (id: number) => void;
}

type ActionType = "linkedin" | null;

interface InlineAction {
  id:   number;
  type: ActionType;
}

export default function CandidatesPage({ onSelect }: Props) {
  const { email: currentUser } = useAuth();

  const [rows,    setRows]    = useState<CandidateListItem[]>([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // UI filter state (controlled inputs — not sent to API until Apply)
  const [filterName,         setFilterName]         = useState("");
  const [filterState,        setFilterState]        = useState("");
  const [filterScrapeStatus, setFilterScrapeStatus] = useState("");
  const [filterPauseReason,  setFilterPauseReason]  = useState("");
  const [filterNoLinkedin,   setFilterNoLinkedin]   = useState(false);

  // Applied filter state (sent to API — only updated on Apply / Clear)
  const [applied, setApplied] = useState({
    name: "", state: "", scrapeStatus: "", pauseReason: "", noLinkedin: false,
  });

  // Inline action state
  const [activeAction,  setActiveAction]  = useState<InlineAction | null>(null);
  const [linkedinValue, setLinkedinValue] = useState("");
  const [actionSaving,  setActionSaving]  = useState(false);
  const [actionError,   setActionError]   = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchCandidates({
      name:          applied.name         || undefined,
      state:         applied.state        || undefined,
      scrape_status: applied.scrapeStatus || undefined,
      pause_reason:  applied.pauseReason  || undefined,
      no_linkedin:   applied.noLinkedin   || undefined,
      limit:  LIMIT,
      offset,
    })
      .then((r: any) => { setRows(r.candidates ?? r.items ?? []); setTotal(r.total ?? 0); })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [applied, offset]);

  useEffect(() => { load(); }, [load]);

  // Update a single row in place without reloading the full list
  const patchRow = (id: number, changes: Partial<CandidateListItem>) => {
    setRows((prev) => prev.map((r) => r.rms_candidate_id === id ? { ...r, ...changes } : r));
  };

  const openAction = (id: number, type: ActionType, currentLinkedin?: string | null) => {
    setActiveAction({ id, type });
    setLinkedinValue(currentLinkedin || "");
    setActionError(null);
  };

  const closeAction = () => {
    setActiveAction(null);
    setActionError(null);
  };

  // ── Save LinkedIn URL ──────────────────────────────────────
  const handleLinkedinSave = async (id: number) => {
    setActionSaving(true);
    setActionError(null);
    try {
      await patchCandidate(id, { linkedin_url: linkedinValue, changed_by: currentUser });
      patchRow(id, { linkedin_url: linkedinValue, scrape_status: "active", pause_reason: null });
      closeAction();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setActionSaving(false);
    }
  };

  // ── Button visibility per row ──────────────────────────────
  const getActions = (r: CandidateListItem): ActionType[] => {
    if (r.scrape_status === "active")            return [];
    if (r.scrape_status === "cannot_be_tracked") return [];
    if (r.scrape_status === "paused")            return ["linkedin"];
    return [];
  };

  return (
    <div className="fyo-page">
      <PageHeader title="Candidates" subtitle="All tracked candidates" />

      {/* ── Filters ── */}
      <div className="fyo-filter-bar">
        <input
          className="fyo-input"
          placeholder="Search by name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setApplied({ name: filterName, state: filterState, scrapeStatus: filterScrapeStatus, pauseReason: filterPauseReason, noLinkedin: filterNoLinkedin }); setOffset(0); } }}
          style={{ width: 200 }}
        />
        <input
          className="fyo-input"
          placeholder="State"
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          style={{ width: 100 }}
        />
        <select className="fyo-select" value={filterScrapeStatus} onChange={(e) => setFilterScrapeStatus(e.target.value)}>
          <option value="">Tracking Statuses</option>
          <option value="active">Active</option>
          <option value="paused">Untrackable</option>
        </select>
        <select className="fyo-select" value={filterPauseReason} onChange={(e) => setFilterPauseReason(e.target.value)}>
          <option value="">All Untrackable Reasons</option>
          <option value="no_linkedin_url">No LinkedIn URL</option>
          <option value="consecutive_failures">Consecutive Failures</option>
        </select>
        <button className="fyo-btn fyo-btn--primary" onClick={() => {
          setApplied({ name: filterName, state: filterState, scrapeStatus: filterScrapeStatus, pauseReason: filterPauseReason, noLinkedin: filterNoLinkedin });
          setOffset(0);
        }}>Apply</button>
        <button className="fyo-btn fyo-btn--ghost" onClick={() => {
          setFilterName(""); setFilterState("");
          setFilterScrapeStatus(""); setFilterPauseReason(""); setFilterNoLinkedin(false);
          setApplied({ name: "", state: "", scrapeStatus: "", pauseReason: "", noLinkedin: false });
          setOffset(0);
        }}>Clear</button>
      </div>

      {error && <div className="fyo-error-banner">{error}</div>}

      <div className="fyo-card fyo-card--no-pad">
        {loading ? (
          <p className="state-text state-text--muted" style={{ padding: "var(--space-6)" }}>Loading...</p>
        ) : (
          <>
            <div className="fyo-table-scroll">
              <table className="fyo-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>State</th>
                  <th>Country</th>
                  <th>LinkedIn</th>
                  <th>Current Employer</th>
                  <th>Candidate Status</th>
                  <th>Untrackable Reason</th>
                  <th>Entered Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="fyo-muted" style={{ textAlign: "center", padding: "var(--space-6)" }}>
                      No candidates found.
                    </td>
                  </tr>
                ) : rows.map((r, i) => {
                  const actions    = getActions(r);
                  const isExpanded = activeAction?.id === r.rms_candidate_id;
                  const odd        = i % 2 === 1;

                  return (
                    <React.Fragment key={r.rms_candidate_id}>
                      <tr
                        className={`${odd ? "fyo-table__row--odd" : ""} fyo-table__row--clickable`}
                        onClick={() => !isExpanded && onSelect(r.rms_candidate_id)}
                      >
                        <td className="fyo-table__cell--primary">{r.display_name}</td>
                        <td>{r.state   || "—"}</td>
                        <td>{r.country || "—"}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {r.linkedin_url
                            ? <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer" className="fyo-link-btn" style={{ fontSize: "var(--font-size-sm)" }}>LinkedIn</a>
                            : <span className="fyo-muted">—</span>}
                        </td>
                        <td>{r.current_employer || "—"}</td>
                        <td><Badge text={r.scrape_status} /></td>
                        <td>{r.pause_reason ? <Badge text={r.pause_reason} /> : "—"}</td>
                        <td>{formatDateLong(r.rms_created_at)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {actions.length > 0 && (
                            <div className="fyo-action-btns">
                              {actions.includes("linkedin") && (
                                <button
                                  className="fyo-btn fyo-btn--warning fyo-btn--sm"
                                  onClick={() => openAction(r.rms_candidate_id, "linkedin", r.linkedin_url)}
                                >
                                  {r.linkedin_url ? "Edit LinkedIn URL" : "Add LinkedIn URL"}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>

                      {/* ── Inline action row ── */}
                      {isExpanded && (
                        <tr className="fyo-table__row--action">
                          <td colSpan={9}>
                            {activeAction?.type === "linkedin" && (
                              <div className="fyo-inline-action">
                                <span className="fyo-inline-action__label">
                                  {r.linkedin_url ? "Edit" : "Add"} LinkedIn URL for <strong>{r.display_name}</strong>
                                </span>
                                <input
                                  className="fyo-input"
                                  style={{ flex: 1, minWidth: 280 }}
                                  value={linkedinValue}
                                  onChange={(e) => setLinkedinValue(e.target.value)}
                                  placeholder="https://linkedin.com/in/..."
                                  autoFocus
                                />
                                {actionError && <span className="text-danger" style={{ fontSize: "var(--font-size-sm)" }}>{actionError}</span>}
                                <button className="fyo-btn fyo-btn--primary fyo-btn--sm"
                                  disabled={actionSaving || !linkedinValue.trim()}
                                  onClick={() => handleLinkedinSave(r.rms_candidate_id)}>
                                  {actionSaving ? "Saving..." : "Save"}
                                </button>
                                <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" onClick={closeAction}>Cancel</button>
                              </div>
                            )}


                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              </table>
            </div>

            <div className="fyo-pagination">
              <span className="fyo-pagination__info">
                {total === 0 ? "No results" : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total}`}
              </span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}>Prev</button>
              <span className="fyo-muted">Page {Math.floor(offset / LIMIT) + 1} of {Math.ceil(total / LIMIT) || 1}</span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset + LIMIT >= total}
                onClick={() => setOffset((o) => o + LIMIT)}>Next</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
